import React, { useState } from "react";
import MainLayouts from "../../Layout/MainLayouts";
import { Link } from "react-router-dom";
import Spinner from "../spinner/Spinner";
import { useAdminStock } from "../../hooks/useInventory";
import { ReactComponent as BoxSeam } from "bootstrap-icons/icons/box-seam.svg";
import { ReactComponent as ExclamationTriangle } from "bootstrap-icons/icons/exclamation-triangle.svg";
import { ReactComponent as XCircle } from "bootstrap-icons/icons/x-circle.svg";

const AdminStockList = () => {
  const [search, setSearch] = useState("");
  const [filterLow, setFilterLow] = useState(false);
  const [filterOut, setFilterOut] = useState(false);

  const { data, isLoading } = useAdminStock({
    search,
    low_stock: filterLow ? 1 : undefined,
    out_of_stock: filterOut ? 1 : undefined,
    per_page: 100,
  });

  const items = data?.data?.inventory || data?.data?.stocks || data?.data || [];
  const stockArray = Array.isArray(items) ? items : [];
  const summary = data?.data?.summary || {};

  const totalItems = summary.total_products ?? stockArray.length;
  const lowStock = summary.low_stock_count ?? stockArray.filter((s) => s.is_low_stock).length;
  const outOfStock = summary.out_of_stock_count ?? stockArray.filter((s) => s.is_out_of_stock).length;

  return (
    <MainLayouts>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Admin Warehouse Inventory</h1>
          <Link
            to="/head-office/inventory/admin-purchases/create"
            className="bg-orange1 text-white px-4 py-2 rounded hover:bg-orange-600 transition"
          >
            + New Purchase
          </Link>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[
            { label: "Total Products", value: totalItems, color: "text-gray-800", Icon: BoxSeam, iconColor: "text-gray-500" },
            { label: "Low Stock", value: lowStock, color: "text-yellow-600", Icon: ExclamationTriangle, iconColor: "text-yellow-500" },
            { label: "Out of Stock", value: outOfStock, color: "text-red-600", Icon: XCircle, iconColor: "text-red-500" },
          ].map((card) => (
            <div key={card.label} className="bg-white rounded-lg shadow p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">{card.label}</p>
                  <p className={`text-3xl font-bold mt-1 ${card.color}`}>{card.value}</p>
                </div>
                <card.Icon className={`w-10 h-10 ${card.iconColor}`} />
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange1 flex-1 min-w-[200px]"
          />
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filterLow} onChange={(e) => setFilterLow(e.target.checked)} className="accent-orange-500" />
            Low Stock Only
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={filterOut} onChange={(e) => setFilterOut(e.target.checked)} className="accent-orange-500" />
            Out of Stock Only
          </label>
        </div>

        {isLoading ? (
          <Spinner loading />
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {["Product", "Category", "Quantity", "Stock Status", "Last Updated"].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stockArray.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-10 text-center text-gray-500">
                        No stock items found. Create a purchase order to add inventory.
                      </td>
                    </tr>
                  ) : (
                    stockArray.map((s, i) => {
                      const qty = s.available_stock ?? s.current_stock ?? s.quantity ?? 0;
                      const isOut = s.is_out_of_stock ?? qty <= 0;
                      const isLow = s.is_low_stock ?? (!isOut && qty <= (s.reorder_level || 10));

                      return (
                        <tr key={s.id || i} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{s.product_name || s.name}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{s.category || "-"}</td>
                          <td className="px-6 py-4 text-sm font-semibold text-gray-900">{qty.toLocaleString()}</td>
                          <td className="px-6 py-4">
                            <span style={{
                              padding: "2px 10px", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600,
                              backgroundColor: isOut ? "#fee2e2" : isLow ? "#fef9c3" : "#dcfce7",
                              color: isOut ? "#991b1b" : isLow ? "#854d0e" : "#166534",
                            }}>
                              {isOut ? "Out of Stock" : isLow ? "Low Stock" : "In Stock"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "-"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </MainLayouts>
  );
};

export default AdminStockList;
